import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  getUserData, 
  purchaseSubscription, 
  getSubscriptionInfo, 
  cancelSubscription, 
  updateVipLevel,
  createTransaction,
  getTransactions,
  getTransaction,
  updateTransaction,
  deleteTransaction,
  getWalletData
} from '../services/api';
import { queryClient } from '../services/queryClient';

export const useUserData = () => {
  return useQuery({
    queryKey: ['user'],
    queryFn: getUserData,
    // Only fetch user data if the user is logged in
    enabled: !!localStorage.getItem('accessToken'),
  });
};

export const useSubscriptionInfo = () => {
  return useQuery({
    queryKey: ['subscription'],
    queryFn: getSubscriptionInfo,
    // Only fetch subscription data if the user is logged in
    enabled: !!localStorage.getItem('accessToken'),
  });
};

export const usePurchaseSubscription = () => {
  return useMutation({
    mutationFn: purchaseSubscription,
    onSuccess: () => {
      // Invalidate and refetch user and subscription data
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
};

export const useCancelSubscription = () => {
  return useMutation({
    mutationFn: cancelSubscription,
    onSuccess: () => {
      // Invalidate and refetch user and subscription data
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
};

export const useUpdateVipLevel = () => {
  return useMutation({
    mutationFn: (vipLevel: number) => updateVipLevel(vipLevel),
    onSuccess: () => {
      // Invalidate and refetch user and subscription data
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });
};

export const useCreateTransaction = () => {
  return useMutation({
    mutationFn: createTransaction,
    onSuccess: () => {
      // Invalidate and refetch transactions and user data
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
};

export const useTransactions = (params?: {
  page?: number;
  pageSize?: number;
  type?: 'add' | 'subtract';
  startDate?: string;
  endDate?: string;
}) => {
  return useQuery({
    queryKey: ['transactions', params],
    queryFn: () => getTransactions(params),
    // Only fetch transactions if the user is logged in
    enabled: !!localStorage.getItem('accessToken'),
  });
};

export const useTransaction = (id: string) => {
  return useQuery({
    queryKey: ['transaction', id],
    queryFn: () => getTransaction(id),
    // Only fetch transaction if the user is logged in and id is provided
    enabled: !!localStorage.getItem('accessToken') && !!id,
  });
};

export const useUpdateTransaction = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateTransaction(id, data),
    onSuccess: () => {
      // Invalidate and refetch transactions
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
};

export const useDeleteTransaction = () => {
  return useMutation({
    mutationFn: (id: string) => deleteTransaction(id),
    onSuccess: () => {
      // Invalidate and refetch transactions and user data
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });
};

export const useWalletData = () => {
  return useQuery({
    queryKey: ['wallet'],
    queryFn: getWalletData,
    // Only fetch wallet data if the user is logged in
    enabled: !!localStorage.getItem('accessToken'),
  });
};
